import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

// Services
import { userService } from '../../services/users';
import { albumsService } from '../../services/albums';
import { artistService } from '../../services/artist';
import { playerService } from '../../services/player';
import { playlistService } from '../../services/playlists';

// Interfaces
import type { RootState } from '../store';
import type { Track } from '../../interfaces/track';
import type { Album } from '../../interfaces/albums';
import type { Artist } from '../../interfaces/artist';
import type { Playlist } from '../../interfaces/playlists';
import { categoriesService } from '../../services/categories';
import { HOME_CACHE_TTL_MS, readHomeCache, writeHomeCache } from '../../utils/homeCache';

// Utils
import { groupBy, uniq, uniqBy } from 'lodash';

// Constants
import { MADE_FOR_YOU_URI, RANKING_URI, TRENDING_URI } from '../../constants/spotify';

const getCacheContext = (state: RootState) => ({
  userId: state.auth.user?.id,
  language: state.language.language,
});

const initialState: {
  topTracks: Track[];
  newReleases: Album[];
  madeForYou: Playlist[];
  featurePlaylists: Playlist[];
  rankings: Playlist[];
  trending: Playlist[];
  recentlyPlayed: (Track | Artist | Album | Playlist)[];
  section: 'ALL' | 'MUSIC' | 'PODCAST';
} = {
  trending: [],
  rankings: [],
  topTracks: [],
  section: 'ALL',
  madeForYou: [],
  newReleases: [],
  recentlyPlayed: [],
  featurePlaylists: [],
};

export const fetchMadeForYou = createAsyncThunk('home/fetchMadeForYou', async (_, { getState }) => {
  const state = getState() as RootState;
  const cacheContext = getCacheContext(state);
  const cached = readHomeCache<Playlist[]>('made-for-you', cacheContext);
  if (cached) return cached;

  const response = await categoriesService.fetchCategoryPlaylists(MADE_FOR_YOU_URI, { limit: 50 });
  const items = response.data.playlists.items;
  writeHomeCache('made-for-you', cacheContext, items, HOME_CACHE_TTL_MS);
  return items;
});

export const fetchRanking = createAsyncThunk('home/fetchRanking', async (_, { getState }) => {
  const state = getState() as RootState;
  const cacheContext = getCacheContext(state);
  const cached = readHomeCache<Playlist[]>('ranking', cacheContext);
  if (cached) return cached;

  const response = await categoriesService.fetchCategoryPlaylists(RANKING_URI, { limit: 10 });
  const items = response.data.playlists.items;
  writeHomeCache('ranking', cacheContext, items, HOME_CACHE_TTL_MS);
  return items;
});

export const fetchTrending = createAsyncThunk('home/fetchTrending', async (_, { getState }) => {
  const state = getState() as RootState;
  const cacheContext = getCacheContext(state);
  const cached = readHomeCache<Playlist[]>('trending', cacheContext);
  if (cached) return cached;

  const response = await categoriesService.fetchCategoryPlaylists(TRENDING_URI, { limit: 10 });
  const items = response.data.playlists.items;
  writeHomeCache('trending', cacheContext, items, HOME_CACHE_TTL_MS);
  return items;
});

export const fetchNewReleases = createAsyncThunk('home/fetchNewReleases', async (_, { getState }) => {
  const state = getState() as RootState;
  const cacheContext = getCacheContext(state);
  const cached = readHomeCache<Album[]>('new-releases', cacheContext);
  if (cached) return cached;

  const response = await albumsService.fetchNewRelases({ limit: 10 });
  const items = response.data.albums.items;
  writeHomeCache('new-releases', cacheContext, items, HOME_CACHE_TTL_MS);
  return items;
});

export const fetchTopTracks = createAsyncThunk('home/fetchTopTracks', async (_, { getState }) => {
  const state = getState() as RootState;
  const cacheContext = getCacheContext(state);
  const cached = readHomeCache<Track[]>('top-tracks', cacheContext);
  if (cached) return cached;

  const response = await userService.fetchTopTracks({ limit: 8, timeRange: 'short_term' });
  const items = response.data.items;
  writeHomeCache('top-tracks', cacheContext, items, HOME_CACHE_TTL_MS);
  return items;
});

export const fetchRecentlyPlayed = createAsyncThunk('home/fetchRecentlyPlayed', async () => {
  try {
    const response = await playerService.getRecentlyPlayed({ limit: 50 });

    const items = response.items.filter((item) =>
      ['artist', 'playlist', 'album'].includes(item.context?.type)
    );

    const groupedItems = groupBy(
      items,
      (item) => item.context.type
    );

    const artistsTracks = groupedItems['artist'] || [];
    const albumsTracks = groupedItems['album'] || [];
    const playlistsTracks = groupedItems['playlist'] || [];

    const artistsIds = uniq(artistsTracks.map((item) => item.context.uri.split(':')[2]));
    const albumsIds = uniq(albumsTracks.map((item) => item.context.uri.split(':')[2]));
    const playlistsIds = uniq(playlistsTracks.map((item) => item.context.uri.split(':')[2]));

    const promises = [
      artistsIds.length
        ? artistService.fetchArtists(artistsIds)
        : Promise.resolve({ data: { artists: [] } }),
      albumsIds.length
        ? albumsService.fetchAlbums(albumsIds)
        : Promise.resolve({ data: { albums: [] } }),
      playlistsIds.length
        ? Promise.all(
            playlistsIds.map(async (id) => {
              const response = await playlistService.getPlaylist(id);
              return response.data;
            })
          )
        : Promise.resolve([]),
    ];

    const [artistsResponse, albumsResponse, playlistsResponse] = await Promise.all(promises);

    // @ts-ignore
    const artists: Artist[] = artistsResponse.data.artists;

    // @ts-ignore
    const albums: Album[] = albumsResponse.data.albums;
    const playlists: Playlist[] = playlistsResponse as Playlist[];

    const tracks = items.map((item) => {
      if (item.context?.type === 'artist') {
        return artists.find((artist) => artist.id === item.context.uri.split(':')[2])!;
      }

      if (item.context?.type === 'album') {
        return albums.find((album) => album.id === item.context.uri.split(':')[2])!;
      }

      if (item.context?.type === 'playlist') {
        return playlists.find((playlist) => playlist.id === item.context.uri.split(':')[2]) ?? item.track;
      }

      return item.track;
    });

    return uniqBy(tracks, 'id');
  } catch (error) {
    console.log(error);
    return [];
  }
});

export const fecthFeaturedPlaylists = createAsyncThunk(
  'home/fecthFeaturedPlaylists',
  async (_, { getState }) => {
    const state = getState() as RootState;
    const cacheContext = getCacheContext(state);
    const cached = readHomeCache<Playlist[]>('featured-playlists', cacheContext);
    if (cached) return cached;

    const response = await playlistService.getFeaturedPlaylists({
      limit: 10,
      locale: state.language.language === 'es' ? 'es_AR' : undefined,
    });
    const items = response.data.playlists.items;
    writeHomeCache('featured-playlists', cacheContext, items, HOME_CACHE_TTL_MS);
    return items;
  }
);

const homeSlice = createSlice({
  name: 'home',
  initialState,
  reducers: {
    setSection(state, action: PayloadAction<'ALL' | 'MUSIC' | 'PODCAST'>) {
      state.section = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchNewReleases.fulfilled, (state, action) => {
      state.newReleases = action.payload as any as any[];
    });
    builder.addCase(fetchTopTracks.fulfilled, (state, action) => {
      state.topTracks = action.payload;
    });
    builder.addCase(fecthFeaturedPlaylists.fulfilled, (state, action) => {
      state.featurePlaylists = action.payload;
    });
    builder.addCase(fetchMadeForYou.fulfilled, (state, action) => {
      state.madeForYou = action.payload;
    });
    builder.addCase(fetchRecentlyPlayed.fulfilled, (state, action) => {
      state.recentlyPlayed = action.payload;
    });
    builder.addCase(fetchRanking.fulfilled, (state, action) => {
      state.rankings = action.payload;
    });
    builder.addCase(fetchTrending.fulfilled, (state, action) => {
      state.trending = action.payload;
    });
  },
});

export const homeActions = {
  ...homeSlice.actions,
  fetchRanking,
  fetchTrending,
  fetchTopTracks,
  fetchMadeForYou,
  fetchNewReleases,
  fetchRecentlyPlayed,
  fecthFeaturedPlaylists,
};

export default homeSlice.reducer;
